import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import { ReactComponent as GearsIcon } from '@elementary/common-ui/assets/gears.svg';
import {
  Chip,
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
  IconButton,
  Radio,
} from '@elementary/common-ui/components';
import {
  capitalize,
  getShortTimeUnitString,
} from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { getWarehouseIcon } from '@elementary/common-ui/utils/environments';
import {
  CircularProgress,
  FormControlLabel,
  Menu,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import useEnvironments from 'shared/hooks/useEnvironments';
import useInitializingEnvs from 'shared/hooks/useInitializingEnvs';
import { HEADER_MENU_VERTICAL_OFFSET } from '@elementary/common-ui/constants/constants';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';
import EnvMenuOptionLabel from './EnvMenuOptionLabel';

const Wrapper = styled(FlexCenter)<{ $isOpen: boolean }>`
  align-items: center;
  cursor: pointer;
  border-left: 1px solid;
  border-right: 1px solid;
  border-color: ${({ theme }) => theme.palette.border.divider};
  height: 100%;
  max-width: 300px;
  padding: ${space(0, 'lg')};
  overflow: hidden;
  background-color: ${({ theme, $isOpen }) =>
    $isOpen ? theme.palette.surface.translucent.pressed : 'transparent'};

  &:hover {
    background-color: ${({ theme }) =>
      theme.palette.surface.translucent.pressed};
  }
`;

const FormControlLabelStyled = styled(FormControlLabel)<{
  $blockPointerEvents: boolean;
  $isSelected: boolean;
}>`
  flex-direction: row-reverse;
  justify-content: space-between;
  margin: 0;
  padding: ${space('lg', 'sm', 'lg', 'lg')};
  pointer-events: ${({ $blockPointerEvents }) =>
    $blockPointerEvents ? 'none' : 'auto'};

  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.palette.surface.selected : ''} !important;

  &:hover {
    background-color: ${({ theme }) => theme.palette.surface.muted} !important;
  }
`;

const ArrowDownIconStyled = styled(ArrowDownIcon)<{ $flip: boolean }>`
  transform: ${({ $flip }) => ($flip ? 'rotate(-180deg)' : undefined)};
  transition: transform 0.1s;
`;

const EnvMenu = () => {
  const [anchorEl, setAnchorEl] = useState<any>();
  const navigate = useNavigate();
  const hasPermission = useAccountPermission();

  const {
    data: envData,
    selectedEnv,
    setSelectedEnv,
    selectedEnvId,
  } = useEnvironments();

  const { isEnvInitializing } = useInitializingEnvs();

  const envOptions = useMemo(() => envData || [], [envData]);

  const lastUpdatedLabel = useMemo(() => {
    const timeFromNow = localizeDate(selectedEnv?.lastSyncedAt).fromNow();
    const formatted = getShortTimeUnitString(timeFromNow);
    return `Updated ${formatted}`;
  }, [selectedEnv]);

  if (!selectedEnvId) {
    return <div />;
  }

  return (
    <>
      <Wrapper
        $isOpen={!!anchorEl}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <FlexColumn sx={{ gap: space('xxs'), width: '100%' }}>
          <Flex sx={{ gap: space('smMinus') }}>
            <FlexCenter sx={{ svg: { width: '16px' } }}>
              {selectedEnv &&
                React.createElement(
                  getWarehouseIcon(selectedEnv.dwhConnection.warehouseType),
                )}
            </FlexCenter>
            <Typography
              noWrap
              sx={{
                fontWeight: fontWeight('semibold'),
              }}
            >
              {capitalize(selectedEnv?.name)}
            </Typography>
            {selectedEnv?.environmentType === 'prod' && (
              <Chip
                label={capitalize(selectedEnv?.environmentType)}
                size="small"
                color="primary"
                bold
                overflow="visible"
              />
            )}
            <FlexCenter sx={{ svg: { width: '8px' } }}>
              <ArrowDownIconStyled $flip={!!anchorEl} />
            </FlexCenter>
          </Flex>

          {isEnvInitializing(selectedEnvId) ? (
            <Flex sx={{ alignItems: 'center', gap: space('smMinus') }}>
              <CircularProgress
                size={8}
                thickness={6}
                sx={{ color: 'status.success.main' }}
              />
              <Typography
                noWrap
                sx={{
                  fontSize: fontSize('sm'),
                  color: 'status.success.main',
                  fontWeight: fontWeight('semibold'),
                }}
              >
                Initializing...
              </Typography>
            </Flex>
          ) : selectedEnv?.lastSyncStatus === 'running' ? (
            <Typography
              noWrap
              sx={{
                fontSize: fontSize('sm'),
                color: 'accent.secondary',
                fontWeight: fontWeight('semibold'),
              }}
            >
              Updating...
            </Typography>
          ) : (
            <Typography noWrap sx={{ fontSize: fontSize('sm') }}>
              {selectedEnv?.lastSyncedAt ? lastUpdatedLabel : ''}
            </Typography>
          )}
        </FlexColumn>
      </Wrapper>

      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: HEADER_MENU_VERTICAL_OFFSET,
          horizontal: 'left',
        }}
        sx={{
          '.MuiMenu-paper': {
            minWidth: '360px',
          },
          '.MuiStack-root': {
            minHeight: '33px',
          },
        }}
      >
        <FlexSpace sx={{ pr: '11px', pl: 2, mb: 1 }}>
          <Typography
            sx={{
              color: 'content.disabled',
              fontSize: fontSize('sm'),
              fontWeight: fontWeight('semibold'),
              letterSpacing: '1px',
            }}
          >
            ENVIRONMENTS
          </Typography>
          {hasPermission('EDIT_INTEGRATIONS') && (
            <IconButton
              onClick={() => {
                navigate('settings/environments');
                setAnchorEl(null);
              }}
            >
              <GearsIcon />
            </IconButton>
          )}
        </FlexSpace>
        <RadioGroup
          value={selectedEnvId}
          onChange={(e) => {
            setSelectedEnv(e.target.value);
            setAnchorEl(null);
          }}
        >
          {envOptions.map((env) => (
            <FormControlLabelStyled
              $blockPointerEvents={envOptions?.length === 0}
              $isSelected={selectedEnvId === env.id}
              key={env.id}
              value={env.id}
              control={
                envOptions?.length > 1 ? <Radio size="small" /> : <div />
              }
              label={
                <EnvMenuOptionLabel
                  env={env}
                  isInitializing={isEnvInitializing(env.id)}
                />
              }
            />
          ))}
        </RadioGroup>
      </Menu>
    </>
  );
};

export default EnvMenu;
